import {
  bucketRelativeTimeMs,
  formatCompactTokenCount as formatTokenUnits,
  type RelativeTimeUnit,
} from "@openclaw/normalization-core";
import { asDateTimestampMs } from "@openclaw/normalization-core/number-coercion";
import { truncateUtf16Safe } from "@openclaw/normalization-core/utf16-slice";
import type { DurationPart } from "../../../src/infra/format-time/format-duration-internal.ts";
import { i18n, t } from "../i18n/index.ts";
import { formatUiError } from "./format-error.ts";

export { formatByteSize } from "@openclaw/normalization-core";

export function formatCountdown(deadlineMs: number, nowMs: number, padMinutes = false): string {
  const totalSeconds = Math.max(0, Math.ceil((deadlineMs - nowMs) / 1_000));
  const minutes = String(Math.floor(totalSeconds / 60));
  return `${padMinutes ? minutes.padStart(2, "0") : minutes}:${String(totalSeconds % 60).padStart(2, "0")}`;
}

type FormatTimeAgoOptions = {
  suffix?: boolean;
  fallback?: string;
};

type FormatRelativeTimestampOptions = {
  dateFallback?: boolean;
  timezone?: string;
  fallback?: string;
  suffix?: boolean;
};

let localeFormatters:
  | {
      locale: string;
      units: Partial<Record<DurationPart["unit"], Intl.NumberFormat>>;
      relative?: Intl.RelativeTimeFormat;
    }
  | undefined;

function getLocaleFormatters() {
  const locale = i18n.getLocale();
  // Keep one locale's closed unit set; switching languages releases the old
  // formatters without retaining labels or subscribing to component lifetimes.
  if (localeFormatters?.locale !== locale) {
    localeFormatters = { locale, units: {} };
  }
  return localeFormatters;
}

export function formatUnit({ value, unit }: DurationPart): string {
  const formatters = getLocaleFormatters();
  return (formatters.units[unit] ??= new Intl.NumberFormat(formatters.locale, {
    style: "unit",
    unit,
    unitDisplay: "narrow",
    maximumFractionDigits: 0,
  })).format(value);
}

function formatRelative(value: number, unit: RelativeTimeUnit): string {
  const formatters = getLocaleFormatters();
  return (formatters.relative ??= new Intl.RelativeTimeFormat(formatters.locale, {
    numeric: "auto",
    style: "narrow",
  })).format(value, unit);
}

export function formatTimeAgo(
  durationMs: number | null | undefined,
  options: FormatTimeAgoOptions = {},
): string {
  const fallback = options.fallback ?? t("common.unknown");
  if (durationMs == null || !Number.isFinite(durationMs) || durationMs < 0) {
    return fallback;
  }

  const { value, unit } = bucketRelativeTimeMs(durationMs);
  if (unit === "second" && options.suffix !== false) {
    return t("common.justNow");
  }
  return options.suffix === false ? formatUnit({ value, unit }) : formatRelative(-value, unit);
}

export function formatRelativeTimestamp(
  timestampMs: number | null | undefined,
  options: FormatRelativeTimestampOptions = {},
): string {
  const fallback = options.fallback ?? t("common.na");
  if (timestampMs == null || !Number.isFinite(timestampMs)) {
    return fallback;
  }

  const diff = timestampMs - Date.now();
  const isPast = diff <= 0;
  const { value, unit } = bucketRelativeTimeMs(Math.abs(diff));
  if (unit === "second") {
    if (options.suffix === false) {
      return formatUnit({ value, unit });
    }
    return isPast ? t("common.justNow") : formatRelative(value, unit);
  }

  if (options.dateFallback && unit === "day" && value > 7) {
    try {
      return new Intl.DateTimeFormat(i18n.getLocale(), {
        month: "short",
        day: "numeric",
        ...(options.timezone ? { timeZone: options.timezone } : {}),
      }).format(new Date(timestampMs));
    } catch {
      // Invalid time zones should still leave a useful localized relative value.
    }
  }

  const signedValue = isPast ? -value : value;
  return options.suffix === false ? formatUnit({ value, unit }) : formatRelative(signedValue, unit);
}

export function formatUnknownText(value: unknown): string {
  if (value == null) {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint" ||
    typeof value === "symbol"
  ) {
    return String(value);
  }
  try {
    const serialized = JSON.stringify(value);
    if (serialized !== undefined) {
      return serialized;
    }
  } catch {
    // Fall back when value is not JSON-serializable.
  }
  if (value instanceof Error) {
    return formatUiError(value);
  }
  return Object.prototype.toString.call(value);
}

export function formatMs(ms?: number | null): string {
  return createMsFormatter()(ms);
}

/** Reuse within one render so the next render picks up locale and system timezone changes.
 * Explicit options replace the default minute-precision fields.
 */
export function createMsFormatter(
  options?: Intl.DateTimeFormatOptions,
  fallback?: string,
): (ms?: number | null) => string {
  let formatter: Intl.DateTimeFormat | undefined;
  return (ms) => {
    const timestampMs = asDateTimestampMs(ms);
    if (timestampMs === undefined) {
      return fallback ?? t("common.na");
    }
    formatter ??= new Intl.DateTimeFormat(
      i18n.getLocale(),
      options ?? {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      },
    );
    return formatter.format(new Date(timestampMs));
  };
}

export function formatDateMs(
  ms?: number | null,
  options?: Intl.DateTimeFormatOptions,
  fallback = t("common.na"),
): string {
  const timestampMs = asDateTimestampMs(ms);
  return timestampMs === undefined
    ? fallback
    : new Date(timestampMs).toLocaleDateString(i18n.getLocale(), options);
}

export function formatTimeMs(
  ms?: number | null,
  options?: Intl.DateTimeFormatOptions,
  fallback = t("common.na"),
): string {
  const timestampMs = asDateTimestampMs(ms);
  return timestampMs === undefined
    ? fallback
    : new Date(timestampMs).toLocaleTimeString(i18n.getLocale(), options ?? { timeStyle: "short" });
}

export function formatDateTimeMs(
  ms?: number | null,
  options?: Intl.DateTimeFormatOptions,
  fallback = t("common.na"),
): string {
  const timestampMs = asDateTimestampMs(ms);
  return timestampMs === undefined
    ? fallback
    : new Date(timestampMs).toLocaleString(i18n.getLocale(), options);
}

export function formatList(values?: Array<string | null | undefined>): string {
  if (!values || values.length === 0) {
    return t("common.none");
  }
  return values.filter((v): v is string => Boolean(v && v.trim())).join(", ");
}

export function clampText(value: string, max = 120): string {
  if (value.length <= max) {
    return value;
  }
  return `${truncateUtf16Safe(value, Math.max(0, max - 1))}…`;
}

export function truncateText(
  value: string,
  max: number,
): {
  text: string;
  truncated: boolean;
  total: number;
} {
  if (value.length <= max) {
    return { text: value, truncated: false, total: value.length };
  }
  return {
    text: truncateUtf16Safe(value, Math.max(0, max)),
    truncated: true,
    total: value.length,
  };
}

export function toNumber(value: string, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function formatCost(cost: number | null | undefined, fallback = "$0.00"): string {
  if (cost == null || !Number.isFinite(cost)) {
    return fallback;
  }
  if (cost === 0) {
    return "$0.00";
  }
  return `$${cost.toFixed(cost < 0.01 ? 4 : cost < 1 ? 3 : 2)}`;
}

// Keep token presentation consistent across UI session and usage surfaces.
export function formatCompactTokenCount(
  tokens: number | null | undefined,
  options: { thousandsSuffix?: string; millionsSuffix?: string; trimTrailingZero?: boolean } = {},
): string {
  if (tokens == null || !Number.isFinite(tokens)) {
    return "0";
  }
  return formatTokenUnits(tokens, {
    thousandsSuffix: options.thousandsSuffix,
    millionsSuffix: options.millionsSuffix ?? "M",
    trimTrailingZero: options.trimTrailingZero ?? true,
    maxUnit: "billion",
  });
}

export function formatContextTokenCapacity(tokens: number): string {
  if (tokens < 1_000_000) {
    return formatCompactTokenCount(tokens);
  }
  return `${Math.floor(tokens / 100_000) / 10}M`;
}
